/* eslint-disable no-undef */

import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { getHeaders } from "../../utility/getHeader";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { CSVLink } from "react-csv";
import { parseISO, format } from "date-fns";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import logo from "../../assets/logo.png"
import { fetchComboData } from "../../services/api";
import CardComponent from "../../components/cards/CardComponent";
import Header from "../../components/Header";
import Layout from "../../layout/Layout";
import DataTable from "../../components/data-tables/dataTableGst";
import fetchData from "../../functions/fetchData";

export default function ItemWiseSummaryVat() {
    let currentDate = format(new Date(), "yyyy-MM-dd");
    
    const [data, setData] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [tableCategories, setTableCategories] = useState([]);
    const [selectedCatId, setSelectedCatId] = useState("");
    const [selectedSubCatId, setSelectedSubCatId] = useState("");
    const [selectedTableCatId, setSelectedTableCatId] = useState("");
    const [filteredData, setFilteredData] = useState([]);
    const [taxType, setTaxType] = useState("VAT");

    const [formdata, setFormData] = useState({
        name: "",
    });
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    // Summary states
    const [showCategorySummary, setShowCategorySummary] = useState(false);
    const [showSubcategorySummary, setShowSubcategorySummary] = useState(false);
    const [showTableCategorySummary, setShowTableCategorySummary] = useState(false);
    const [categorySummaryData, setCategorySummaryData] = useState([]);
    const [subcategorySummaryData, setSubcategorySummaryData] = useState([]);
    const [tableCategorySummaryData, setTableCategorySummaryData] = useState([]);

    // Column definitions for VAT-specific display
    const columns = [
        { label: "Item Code", field: "item_code", fallback: ["code"] },
        { label: "Item Name", field: "item_name", fallback: ["name", "product_name"] },
        { label: "Category", field: "category_name", fallback: ["catname"] },
        { label: "Subcategory", field: "subcategory_name", fallback: ["subcatname"] },
        { label: "Total Bills", field: "invoice_number", fallback: ["total_bills"] },
        { label: "Last Sale Date", field: "setup_date", fallback: ["last_sale_date", "date"] },
        { label: "Quantity", field: "quantity", fallback: ["qty", "total_quantity_sold"] },
        { label: "UOM", field: "uom", fallback: ["unit"] },
        { label: "Rate", field: "rate", fallback: ["price", "unit_price", "average_selling_price"] },
        { label: "VAT %", field: "vat_rate", fallback: ["vat", "cgst", "tax_rate"] },
        { label: "VAT Amount", field: "vat_amount", fallback: ["tax_amount"] },
        { label: "Total", field: "total_price", fallback: ["total", "total_amount", "total_sales_amount"] },
    ];

    const categorySummaryColumns = [
        { label: "Category Name", field: "category_name" },
        { label: "Total Quantity", field: "total_quantity" },
        { label: "Total VAT", field: "total_vat" },
        { label: "Total Amount", field: "total_amount" },
    ];

    const subcategorySummaryColumns = [
        { label: "Subcategory Name", field: "subcategory_name" },
        { label: "Total Quantity", field: "total_quantity" },
        { label: "Total VAT", field: "total_vat" },
        { label: "Total Amount", field: "total_amount" },
    ];

    const tableCategorySummaryColumns = [
        { label: "Table Category Name", field: "table_category_name" },
        { label: "Total Quantity", field: "total_quantity" },
        { label: "Total VAT", field: "total_vat" },
        { label: "Total Amount", field: "total_amount" },
    ];

    // Summary generation functions
    const generateCategorySummary = async () => {
        try {
            const params = {};
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;
            if (selectedCatId) params.category_id = selectedCatId;
            
            const resData = await axios.get("/reports/category-wise-sales", {
                ...getHeaders(),
                params
            });
            
            const categories = resData.data?.data?.categories || [];
            
            const summaryData = categories.map(cat => {
                const totalQuantity = parseFloat(cat.total_quantity_sold || 0);
                const totalAmount = parseFloat(cat.total_sales_amount || 0);
                const vatRate = 7.00; // Default VAT rate
                const totalVat = totalAmount - (totalAmount / (1 + vatRate / 100));
                
                return {
                    category_id: cat.category_id,
                    category_name: cat.category_name || cat.catname || 'Uncategorized',
                    total_quantity: parseFloat(totalQuantity).toFixed(2),
                    total_vat: parseFloat(totalVat).toFixed(2),
                    total_amount: parseFloat(totalAmount).toFixed(2),
                    total_items: cat.total_items || 0,
                    total_bills: cat.total_bills || 0
                };
            });
            
            console.log("Category summary from backend:", summaryData);
            setCategorySummaryData(summaryData);
            setShowCategorySummary(true);
            setShowSubcategorySummary(false);
            setShowTableCategorySummary(false);
        } catch (error) {
            console.error("Error fetching category summary:", error);
            toast.error("Failed to load category summary");
        }
    };

    const generateSubcategorySummary = async () => {
        try {
            const params = {};
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;
            if (selectedCatId) params.category_id = selectedCatId;
            if (selectedSubCatId) params.subcategory_id = selectedSubCatId;
            
            const resData = await axios.get("/reports/subcategory-wise-sales", {
                ...getHeaders(),
                params
            });
            
            const subcategories = resData.data?.data?.subcategories || [];
            
            const summaryData = subcategories.map(subcat => {
                const totalQuantity = parseFloat(subcat.total_quantity_sold || 0);
                const totalAmount = parseFloat(subcat.total_sales_amount || 0);
                const vatRate = 7.00; // Default VAT rate
                const totalVat = totalAmount - (totalAmount / (1 + vatRate / 100));
                
                return {
                    subcategory_id: subcat.subcategory_id,
                    subcategory_name: subcat.subcategory_name || subcat.subcatname || 'Uncategorized',
                    category_name: subcat.category_name || subcat.catname || '',
                    total_quantity: parseFloat(totalQuantity).toFixed(2),
                    total_vat: parseFloat(totalVat).toFixed(2),
                    total_amount: parseFloat(totalAmount).toFixed(2),
                    total_items: subcat.total_items || 0,
                    total_bills: subcat.total_bills || 0
                };
            });
            
            console.log("Subcategory summary from backend:", summaryData);
            setSubcategorySummaryData(summaryData);
            setShowSubcategorySummary(true);
            setShowCategorySummary(false);
            setShowTableCategorySummary(false);
        } catch (error) {
            console.error("Error fetching subcategory summary:", error);
            toast.error("Failed to load subcategory summary");
        }
    };

    const generateTableCategorySummary = () => {
        // Use the already filtered data that includes all active filters (date, item name, category, etc.)
        const dataToSummarize = filteredData.length > 0 ? filteredData : data;
        const summaryMap = {};

        console.log("=== TABLE CATEGORY SUMMARY DEBUG ===");
        console.log("Data to summarize count:", dataToSummarize.length);
        console.log("Active filters:", { startDate, endDate, selectedTableCatId, selectedCatId, selectedSubCatId, itemName: formdata.name });
        console.log("Sample data:", dataToSummarize[0]);

        dataToSummarize.forEach(item => {
            const tableCategoryId = item.table_cat_id || item.table_category_id;
            if (!tableCategoryId) {
                console.log("Skipping item without table_cat_id:", item.item_name || item.name);
                return;
            }

            const tableCategory = tableCategories.find(tcat => tcat.id.toString() === tableCategoryId.toString());
            const tableCatName = tableCategory ? tableCategory.cat_name : `Unknown (ID: ${tableCategoryId})`;

            if (!summaryMap[tableCategoryId]) {
                summaryMap[tableCategoryId] = { 
                    table_category_name: tableCatName, 
                    total_quantity: 0,
                    total_vat: 0,
                    total_amount: 0 
                };
                console.log("Created new summary entry for table category:", tableCatName, "ID:", tableCategoryId);
            }

            // Handle different field names for amounts
            const quantity = parseFloat(item.quantity || item.qty || 0);
            const vatAmount = parseFloat(item.vat_amount || item.tax_amount || 0);
            const totalAmount = parseFloat(item.total_price || item.total || item.total_amount || 0);

            summaryMap[tableCategoryId].total_quantity += quantity;
            summaryMap[tableCategoryId].total_vat += vatAmount;
            summaryMap[tableCategoryId].total_amount += totalAmount;
        });

        const summaryArray = Object.values(summaryMap);
        console.log("Generated table category summary:", summaryArray);
        setTableCategorySummaryData(summaryArray);
        setShowTableCategorySummary(true);
        setShowCategorySummary(false);
        setShowSubcategorySummary(false);
    };
    // Filtering function - refetch data with filters applied
    const applyFilter = async () => {
        // Validate date range
        if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
            toast.warn("Invalid date range: Start date is after end date");
            return;
        }
        
        try {
            const params = {};
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;
            
            const resData = await axios.get("/reports/item-wise-sales", {
                ...getHeaders(),
                params
            });
            
            const items = resData.data?.data?.items || [];
            
            // Transform and filter data
            let processedData = items.map(item => {
                const quantity = parseFloat(item.total_quantity_sold || 0);
                const totalPrice = parseFloat(item.total_sales_amount || 0);
                const avgPrice = parseFloat(item.average_selling_price || 0);
                const vatRate = 7.00;
                const calculatedVatAmount = totalPrice - (totalPrice / (1 + vatRate / 100));
                const calculatedRate = avgPrice > 0 ? avgPrice : (quantity > 0 ? (totalPrice / (1 + vatRate / 100)) / quantity : 0);
                
                return {
                    item_id: item.item_id,
                    item_code: item.item_code || 'N/A',
                    item_name: item.item_name || 'Unknown',
                    invoice_number: (item.total_bills || 0) + ' Bills',
                    total_bills: item.total_bills || 0,
                    setup_date: item.last_sale_date || item.first_sale_date || '',
                    quantity: parseFloat(quantity).toFixed(2),
                    uom: item.unit || 'PCS',
                    rate: parseFloat(calculatedRate).toFixed(2),
                    vat_rate: parseFloat(vatRate).toFixed(2),
                    vat_amount: parseFloat(calculatedVatAmount).toFixed(2),
                    total_price: parseFloat(totalPrice).toFixed(2),
                    category_name: item.category_name || 'No Category',
                    subcategory_name: item.subcategory_name || 'No Subcategory',
                    catid: null,
                    subcatid: null,
                    first_sale_date: item.first_sale_date,
                    last_sale_date: item.last_sale_date
                };
            });
            
            // Apply client-side filters for item name and categories
            if (formdata.name) {
                processedData = processedData.filter(item => 
                    item.item_name?.toLowerCase().includes(formdata.name.toLowerCase())
                );
            }
            
            if (selectedCatId) {
                const category = categories.find(cat => cat.id.toString() === selectedCatId.toString());
                if (category) {
                    processedData = processedData.filter(item => 
                        item.category_name === category.name
                    );
                }
            }
            
            if (selectedSubCatId) {
                const subcategory = subcategories.find(sub => sub.id.toString() === selectedSubCatId.toString());
                if (subcategory) {
                    processedData = processedData.filter(item => 
                        item.subcategory_name === subcategory.subcat
                    );
                }
            }
            
            console.log("Filtered data:", processedData);
            setFilteredData(processedData);
            toast.success("Filter applied successfully");
        } catch (error) {
            console.error("Error applying filter:", error);
            toast.error("Failed to apply filter");
        }
    };

    // Clear filters
    const clearFilters = () => {
        setStartDate("");
        setEndDate("");
        setFormData({ name: "" });
        setSelectedCatId("");
        setSelectedSubCatId("");
        setSelectedTableCatId("");
        setFilteredData(data);
        setShowCategorySummary(false);
        setShowSubcategorySummary(false);
        setShowTableCategorySummary(false);
        setCategorySummaryData([]);
        setSubcategorySummaryData([]);
        setTableCategorySummaryData([]);
    };

    // Export to PDF
    const exportPDF = () => {
        const doc = new jsPDF();
        const exportData = filteredData.length > 0 ? filteredData : data;

        // Add logo
        doc.addImage(logo, "PNG", 150, 10, 40, 15);

        doc.setFontSize(16);
        doc.text("Item Wise VAT Summary", 14, 20);

        const tableColumn = [
            "Invoice No", "Date", "Item", "Qty", "UOM", "Rate",
            "VAT %", "VAT Amount", "Total"
        ];

        const tableRows = [];
        let vatTotal = 0;
        let grandTotal = 0;

        exportData.forEach((item) => {
            // Handle different field names
            const invoiceNo = item.invoice_number || item.invoice_no || item.bill_no;
            const date = item.setup_date;
            const itemName = item.item_name || item.name || item.product_name;
            const quantity = item.quantity || item.qty;
            const rate = item.rate || item.price || item.unit_price;
            const vatRate = item.vat_rate || item.cgst || item.tax_rate;
            const vatAmount = item.vat_amount || item.tax_amount;
            const totalPrice = item.total_price || item.total || item.total_amount;

            tableRows.push([
                invoiceNo,
                date ? format(new Date(date), "yyyy-MM-dd") : "",
                itemName,
                quantity,
                item.uom || item.unit,
                rate,
                vatRate,
                vatAmount,
                totalPrice,
            ]);

            vatTotal += parseFloat(vatAmount || 0);
            grandTotal += parseFloat(totalPrice || 0);
        });

        doc.autoTable({
            startY: 30,
            head: [tableColumn],
            body: tableRows,
        });

        const finalY = doc.autoTable.previous.finalY + 10;

        doc.setFontSize(12);
        doc.text("Summary", 14, finalY);
        doc.text(`Total VAT: ${vatTotal.toFixed(2)}`, 14, finalY + 8);
        doc.text(`Total Amount: ${grandTotal.toFixed(2)}`, 14, finalY + 16);

        doc.save("itemwise_vat_summary.pdf");
    };

    // Export to Excel
    const exportExcel = () => {
        const exportData = filteredData.length > 0 ? filteredData : data;
        
        const worksheetData = exportData.map(item => {
            // Handle different field names
            const invoiceNo = item.invoice_number || item.invoice_no || item.bill_no;
            const date = item.setup_date;
            const itemName = item.item_name || item.name || item.product_name;
            const categoryName = item.category_name || item.cat_name;
            const subcategoryName = item.subcategory_name || item.subcat_name || item.sub_category;
            const tableCategoryName = item.table_category_name || item.table_cat_name;
            const quantity = item.quantity || item.qty;
            const rate = item.rate || item.price || item.unit_price;
            const vatRate = item.vat_rate || item.cgst || item.tax_rate;
            const vatAmount = item.vat_amount || item.tax_amount;
            const totalPrice = item.total_price || item.total || item.total_amount;

            return {
                "Invoice No": invoiceNo,
                "Date": date ? format(new Date(date), "yyyy-MM-dd") : "",
                "Category": categoryName,
                "Subcategory": subcategoryName,
                "Table Category": tableCategoryName,
                "Item Name": itemName,
                "Quantity": quantity,
                "UOM": item.uom || item.unit,
                "Rate": rate,
                "VAT %": vatRate,
                "VAT Amount": vatAmount,
                "Total": totalPrice
            };
        });

        // Add summary row
        const vatTotal = exportData.reduce((acc, item) => {
            const vatAmount = item.vat_amount || item.tax_amount;
            return acc + parseFloat(vatAmount || 0);
        }, 0);
        
        const grandTotal = exportData.reduce((acc, item) => {
            const totalPrice = item.total_price || item.total || item.total_amount;
            return acc + parseFloat(totalPrice || 0);
        }, 0);
        
        worksheetData.push({});
        worksheetData.push({
            "Invoice No": "TOTALS",
            "VAT Amount": vatTotal.toFixed(2),
            "Total": grandTotal.toFixed(2)
        });

        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "VAT Summary");

        XLSX.writeFile(workbook, "itemwise_vat_summary.xlsx");
    };

    // Data fetching useEffects
    useEffect(() => {
        const fetchDataAndCategories = async () => {
            try {
                // Use the new backend report route with optional date filters
                const params = {};
                if (startDate) params.start_date = startDate;
                if (endDate) params.end_date = endDate;
                
                const resData = await axios.get("/reports/item-wise-sales", {
                    ...getHeaders(),
                    params
                });
                
                console.log("Fetched data from new endpoint:", resData.data); // Debug log
                console.log("Sample item from backend:", resData.data?.data?.items?.[0]); // Debug backend structure
                
                // Transform the backend response to match expected format
                const items = resData.data?.data?.items || [];
                
                const processedData = items.map(item => {
                    const quantity = parseFloat(item.total_quantity_sold || 0);
                    const totalPrice = parseFloat(item.total_sales_amount || 0);
                    const avgPrice = parseFloat(item.average_selling_price || 0);
                    
                    // Get VAT rate from item (default 7% if not available)
                    const vatRate = 7.00; // You can adjust this or fetch from item data
                    
                    // Calculate VAT amount: vat_amount = total_price - (total_price / (1 + vat_rate/100))
                    const calculatedVatAmount = totalPrice - (totalPrice / (1 + vatRate / 100));
                    
                    // Calculate unit rate (price before VAT)
                    const calculatedRate = avgPrice > 0 ? avgPrice : (quantity > 0 ? (totalPrice / (1 + vatRate / 100)) / quantity : 0);
                    
                    return {
                        item_id: item.item_id,
                        item_code: item.item_code || 'N/A',
                        item_name: item.item_name || 'Unknown',
                        invoice_number: (item.total_bills || 0) + ' Bills',
                        total_bills: item.total_bills || 0,
                        setup_date: item.last_sale_date || item.first_sale_date || '',
                        quantity: parseFloat(quantity).toFixed(2),
                        uom: item.unit || 'PCS',
                        rate: parseFloat(calculatedRate).toFixed(2),
                        vat_rate: parseFloat(vatRate).toFixed(2),
                        vat_amount: parseFloat(calculatedVatAmount).toFixed(2),
                        total_price: parseFloat(totalPrice).toFixed(2),
                        category_name: item.category_name || 'No Category',
                        subcategory_name: item.subcategory_name || 'No Subcategory',
                        catid: null,
                        subcatid: null,
                        first_sale_date: item.first_sale_date,
                        last_sale_date: item.last_sale_date
                    };
                });
                
                console.log("Processed data sample:", processedData[0]); // Debug log
                setData(processedData);
                setFilteredData(processedData);
            } catch (error) {
                console.error("Error fetching item-wise sales report:", error);
                toast.error("Failed to load item-wise sales report");
            }
        };
        fetchDataAndCategories();
    }, [startDate, endDate]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const cats = await fetchComboData("categories", "name");
                setCategories(cats);
            } catch (err) {
                console.error("Error fetching categories:", err);
            }
        };
        fetchCategories();
    }, []);

    useEffect(() => {
        const fetchTableCategories = async () => {
            try {
                const tableCats = await fetchComboData("table_category", "cat_name");
                setTableCategories(tableCats);
            } catch (err) {
                console.error("Error fetching table categories:", err);
            }
        };
        fetchTableCategories();
    }, []);

    useEffect(() => {
        const fetchSubcategories = async () => {
            if (!selectedCatId) {
                setSubcategories([]);
                setSelectedSubCatId("");
                return;
            }
            try {
                const subs = await fetchComboData("subcategory", "subcat", { catid: selectedCatId });
                setSubcategories(subs);
            } catch (err) {
                console.error("Error fetching subcategories:", err);
            }
        };
        fetchSubcategories();
    }, [selectedCatId]);

    // Note: Removed auto-filter useEffect to prevent infinite loops
    // Filters are now applied manually via "Apply Filter" button or on initial data load

    return (
        <>
            <Layout>
                <Header title="Item Wise VAT Summary" />
                <ToastContainer />
                <div className="row">
                    <div className="col-12">
                        <CardComponent>
                            {/* Filter Section */}
                            <div className="row mb-3">
                                <div className="col-lg-3 col-md-4 col-sm-6 col-xs-12">
                                    <label>Start Date</label>
                                    <input 
                                        type="date" 
                                        className="form-control" 
                                        value={startDate} 
                                        onChange={(e) => setStartDate(e.target.value)} 
                                    />
                                </div>
                                <div className="col-lg-3 col-md-3 col-sm-6 col-xs-6">
                                    <label>End Date</label>
                                    <input 
                                        type="date" 
                                        className="form-control" 
                                        value={endDate} 
                                        onChange={(e) => setEndDate(e.target.value)} 
                                    />
                                </div>
                                <div className="col-lg-3 col-md-3 col-sm-6 col-xs-6">
                                    <label>Item Name</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formdata.name}
                                        onChange={(e) => setFormData({ ...formdata, name: e.target.value })}
                                        placeholder="Enter item name"
                                    />
                                </div>
                                <div className="col-lg-3 col-md-3 col-sm-6 col-xs-6">
                                    <label>Category</label>
                                    <select
                                        className="form-control"
                                        value={selectedCatId}
                                        onChange={(e) => setSelectedCatId(e.target.value)}
                                    >
                                        <option value="">All Categories</option>
                                        {categories.map(cat => (
                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-lg-3 col-md-3 col-sm-6 col-xs-6">
                                    <label>Subcategory</label>
                                    <select
                                        className="form-control"
                                        value={selectedSubCatId}
                                        onChange={(e) => setSelectedSubCatId(e.target.value)}
                                    >
                                        <option value="">All Subcategories</option>
                                        {subcategories.map(sub => (
                                            <option key={sub.id} value={sub.id}>{sub.subcat}</option>
                                        ))}
                                    </select>
                                </div>
                               
                            </div>

                            {/* Action Buttons */}
                            <div className="row mt-3">
                                <div className="col-12">
                                    <button className="btn btn-primary me-2" onClick={applyFilter}>
                                        Apply Filter
                                    </button>
                                    <button className="btn btn-info me-2" onClick={clearFilters}>
                                        Clear Filters
                                    </button>
                                    <button className="btn btn-danger me-2" onClick={exportPDF}>
                                        Export PDF
                                    </button>
                                    <button className="btn btn-success me-2" onClick={exportExcel}>
                                        Export Excel
                                    </button>
                                </div>
                            </div>

                          

                            {/* Back to Details Buttons */}
                            {(showCategorySummary || showSubcategorySummary || showTableCategorySummary) && (
                                <div className="row mt-2">
                                    <div className="col-12">
                                        <button
                                            className="btn btn-secondary"
                                            onClick={() => {
                                                setShowCategorySummary(false);
                                                setShowSubcategorySummary(false);
                                                setShowTableCategorySummary(false);
                                            }}
                                        >
                                            Back to Item Details
                                        </button>
                                    </div>
                                </div>
                            )}
                        </CardComponent>
                    </div>

                    {/* Data Table Section */}
                    <div className="col-12">
                        {data.length === 0 ? (
                            <p>No data available</p>
                        ) : (
                            <DataTable
                                columns={
                                    showCategorySummary
                                        ? categorySummaryColumns
                                        : showSubcategorySummary
                                        ? subcategorySummaryColumns
                                        : showTableCategorySummary
                                        ? tableCategorySummaryColumns
                                        : columns
                                }
                                data={
                                    showCategorySummary
                                        ? categorySummaryData
                                        : showSubcategorySummary
                                        ? subcategorySummaryData
                                        : showTableCategorySummary
                                        ? tableCategorySummaryData
                                        : (filteredData.length > 0 ? filteredData : data)
                                }
                                tablename={
                                    showCategorySummary
                                        ? "category_vat_summary"
                                        : showSubcategorySummary
                                        ? "subcategory_vat_summary"
                                        : showTableCategorySummary
                                        ? "table_category_vat_summary"
                                        : "item_wise_vat_summary"
                                }
                            />
                        )}

                        {/* Summary Totals */}
                        <div className="mt-3 p-3 bg-light rounded">
                            <div className="row">
                                <div className="col-md-4">
                                    <strong>Total Items:</strong> {(filteredData.length > 0 ? filteredData : data).length}
                                </div>
                                <div className="col-md-4">
                                    <strong>Total VAT:</strong> ฿{
                                        (filteredData.length > 0 ? filteredData : data)
                                            .reduce((acc, item) => {
                                                const vatAmount = item.vat_amount || item.tax_amount;
                                                return acc + parseFloat(vatAmount || 0);
                                            }, 0)
                                            .toFixed(2)
                                    }
                                </div>
                                <div className="col-md-4">
                                    <strong>Grand Total:</strong> ฿{
                                        (filteredData.length > 0 ? filteredData : data)
                                            .reduce((acc, item) => {
                                                const totalPrice = item.total_price || item.total || item.total_amount;
                                                return acc + parseFloat(totalPrice || 0);
                                            }, 0)
                                            .toFixed(2)
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Layout>
        </>
    );
}
